import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { filter } from 'rxjs';

import {
  CoreEventSource,
  RemoveHotelSelectionFiltersOrigin,
} from '@apps/main/new-desktop/core/models';
import {
  useBrowserStore,
  useEditingIngredientStore,
  useItineraryStore,
  useMetaStore,
  usePaxStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import type { HotelFilterUpdate } from '@apps/main/new-desktop/modules/hotelFilters';
import { useFerryFiltersStore } from '@apps/main/new-desktop/core/stores/filters/ferryFiltersStore';
import { useConfigurationPanelStore } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import { assertValue, isDefined, asObservable } from '@libs/utils';
import { isAvailableHotelIngredientResponse } from '@libs/search-service-smart-search';
import type { IngredientId, Pax } from '@libs/trip-planner-interfaces';
import { blockBackgroundScroll, enableBackgroundScroll } from '@libs/ui';

import type { TripOverviewPanel, TripOverviewPanelType } from '../models';
import {
  TRIP_OVERVIEW_HOTEL_FILTERS_PANEL,
  TRIP_OVERVIEW_CONFIGURATION_PANEL,
  TRIP_OVERVIEW_FERRY_FILTERS_PANEL,
  TRIP_OVERVIEW_NO_PANEL,
  TRIP_PRICE_SUMMARY_PRE_CHECKOUT_PANEL,
} from '../models';

export const useTripOverviewPanelsStore = defineStore(
  'TripOverviewPanelStore',
  () => {
    const recipeStore = useRecipeStore();
    const itineraryStore = useItineraryStore();
    const statsigEventsStore = useStatsigEventsStore();
    const { selectedItinerary } = storeToRefs(itineraryStore);
    const metaStore = useMetaStore();
    const paxStore = usePaxStore();
    const configurationPanelStore = useConfigurationPanelStore();
    const editingIngredientStore = useEditingIngredientStore();
    const filtersStore = useFiltersStore();
    const ferryFiltersStore = useFerryFiltersStore();
    const routerStore = useRouterStore();
    const browserStore = useBrowserStore();

    // State
    const tripOverviewPanel = ref<TripOverviewPanel>(TRIP_OVERVIEW_NO_PANEL);

    // Watchers
    watch(
      () => tripOverviewPanel.value,
      () => {
        if (browserStore.isSmallerThan900px) {
          if (tripOverviewPanel.value.type === TRIP_OVERVIEW_NO_PANEL.type) {
            enableBackgroundScroll();
          } else {
            blockBackgroundScroll();
          }
        }
      },
      { immediate: true }
    );

    // Actions
    function showMetaHotelDetails(): void {
      const selectedItinerarySubscription = asObservable(selectedItinerary)
        .pipe(filter(isDefined))
        .subscribe((itinerary) => {
          const firstHotelIngredient = itinerary.hotelOnlyItinerary[0];
          assertValue(
            firstHotelIngredient,
            'tripOverviewPanelsStore.ts > showMetaHotelDetails > firstHotelIngredient'
          );

          const selectedHotelCode = isAvailableHotelIngredientResponse(
            firstHotelIngredient
          )
            ? firstHotelIngredient.suggested.code
            : undefined;
          const ingredientId = firstHotelIngredient.ingredientId;

          metaStore.metaHotelCode = selectedHotelCode;

          const ingredientIndex = recipeStore.getIngredientIndex(ingredientId);

          if (selectedHotelCode) {
            routerStore.navigateToHotelDetailsInTripOverview(
              { ingredientIndex: ingredientIndex },
              selectedHotelCode,
              true
            );
            selectedItinerarySubscription.unsubscribe();
          }
        });
    }

    function closeTripOverviewPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function openTripPriceSummaryPreCheckoutPanel(): void {
      tripOverviewPanel.value = TRIP_PRICE_SUMMARY_PRE_CHECKOUT_PANEL;
    }

    function closeTripPriceSummaryPreCheckoutPanel(): void {
      closeTripOverviewPanel();
    }

    function openConfigurationPanel(
      tabKey: TabKey,
      source:
        | 'overview-header-actions'
        | 'date-location-segment'
        | 'low-price-calendar'
        | 'auto'
    ): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_CONFIGURATION_PANEL;
      configurationPanelStore.handleTabSwitch(tabKey);

      if (tabKey === TabKey.Pax) {
        statsigEventsStore.logCorePaxModalOpened({
          source: CoreEventSource.TripOverview,
        });
      }

      statsigEventsStore.logTripOverviewConfigurationPanelOpened({
        source,
        tab: tabKey,
      });
    }

    function closeConfigurationPanel(): void {
      paxStore.clearWarningMessages();
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function updatePax(pax: Pax): void {
      if (pax.length) {
        paxStore.updatePax(pax, CoreEventSource.TripOverview);
      }
    }

    const tripOverviewPanelType = computed<TripOverviewPanelType>(
      () => tripOverviewPanel.value.type
    );

    function openHotelFiltersPanel(ingredientId: IngredientId): void {
      const hotelIngredient = recipeStore.getHotelIngredient(ingredientId);
      editingIngredientStore.setStayEditingIngredient(hotelIngredient);
      filtersStore.setHotelIngredientForFiltering();

      tripOverviewPanel.value = TRIP_OVERVIEW_HOTEL_FILTERS_PANEL;
    }

    function closeHotelFiltersPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    function applyHotelFilters(): void {
      const newHotelIngredient = filtersStore.getChangedHotelIngredient();
      if (newHotelIngredient && editingIngredientStore.editingStayIngredient) {
        editingIngredientStore.editingStayIngredient.filters =
          newHotelIngredient.filters;
      }
      filtersStore.clearEditedHotelIngredient();
      closeHotelFiltersPanel();
    }

    function updateHotelFilters(event: HotelFilterUpdate): void {
      filtersStore.updateHotelFilter(
        event,
        RemoveHotelSelectionFiltersOrigin.TripOverview,
        false
      );
    }

    function resetHotelFilters(): void {
      filtersStore.resetHotelFilters();
    }

    function openFerryFiltersPanel(ingredientId: IngredientId): void {
      ferryFiltersStore.setAllowedFerryIngredientIds([ingredientId]);
      tripOverviewPanel.value = TRIP_OVERVIEW_FERRY_FILTERS_PANEL;
    }

    function closeFerryFiltersPanel(): void {
      tripOverviewPanel.value = TRIP_OVERVIEW_NO_PANEL;
    }

    return {
      // Actions
      openTripPriceSummaryPreCheckoutPanel,
      closeTripPriceSummaryPreCheckoutPanel,
      closeTripOverviewPanel,
      closeConfigurationPanel,
      updatePax,
      openConfigurationPanel,
      showMetaHotelDetails,
      openHotelFiltersPanel,
      closeHotelFiltersPanel,
      applyHotelFilters,
      updateHotelFilters,
      resetHotelFilters,
      openFerryFiltersPanel,
      closeFerryFiltersPanel,

      // Getters
      tripOverviewPanelType,
    };
  }
);
